import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  type JSX,
  type Ref,
} from 'react';
import { useIntl } from 'react-intl';

import type { DataTableColumnSettingsModalRef } from './column-settings-types.js';
import {
  generateColumnOrderWithParentIds,
  generateColumnVisibilityObject,
  getCombinedVisibilityCheckboxState,
  getHasColumnOrderChanged,
  getHasColumnPinningChanged,
  getHasVisibilityChanged,
  getMultipleLocalColumnVisibility,
} from './column-settings-utils.js';
import { DataTableColumnsList } from './DataTableColumnsList.js';
import { DataTableVisibilityFilterControls } from './DataTableVisibilityFilterControls.js';
import { useDataTable } from '../../../contexts/DataTableContext.js';
import type { DataTablePinnedColumnState } from '../../../features/ColumnPinning/column-pinning-types.js';
import type { DataTableColumnVisibilityState } from '../../../features/ColumnVisibility/column-visibility-types.js';
import type { Column } from '../../../hooks/useTable/types.js';
import { dataTableMessages } from '../../../messages.js';
import type { DataTableColumnSettingsConfig } from '../../../public.api.js';
import type { ColumnPinningState } from '../../../tanstack-table-core-override/src/index.js';
import { DataTableSettingsModal } from '../../SettingsModal/DataTableSettingsModal.js';

interface DataTableColumnSettingsModalProps {
  /**
   * Whether column visibility settings are available in the modal.
   */
  visibilitySettings: boolean;
  /**
   * Whether column order settings are available in the modal.
   */
  columnOrderSettings: boolean;
  /**
   * Whether column pinning settings are available in the modal.
   */
  columnPinningSettings: boolean;
  /**
   * Default column visibility when settings are reset.
   */
  resetColumnVisibility?: DataTableColumnVisibilityState;
  /**
   * Default column order when settings are reset.
   */
  resetColumnOrder?: string[];
  /**
   * Default column pinning state when settings are reset.
   */
  resetColumnPinning?: DataTablePinnedColumnState;
}

/**
 * Modal combining UI controls for column visibility and column ordering.
 * All changes that made within the modal are only applied once the user click the Confirm button.
 * When clicking Close, the changes won't be applied and discarded.
 */
const ColumnSettingsModal = (
  props: DataTableColumnSettingsModalProps & {
    onModalClose: () => void;
    columnSettingsOverride?: DataTableColumnSettingsConfig;
  },
): JSX.Element => {
  const {
    visibilitySettings: toolbarVisibilitySettings,
    columnOrderSettings: toolbarColumnOrderSettings,
    columnPinningSettings: toolbarColumnPinningSettings,
    resetColumnOrder,
    resetColumnVisibility,
    resetColumnPinning,
    onModalClose,
    columnSettingsOverride,
  } = props;

  const intl = useIntl();
  const table = useDataTable();
  const leafColumnsWithoutBuiltin = table.getLeafColumnsWithoutBuiltin();

  const visibilitySettingsEnabled =
    columnSettingsOverride?.columnVisibility ?? toolbarVisibilitySettings;
  const columnOrderSettingsEnabled =
    columnSettingsOverride?.columnOrder ?? toolbarColumnOrderSettings;
  const columnPinningSettingsEnabled =
    columnSettingsOverride?.columnPinning ?? toolbarColumnPinningSettings;

  // original column order state
  const {
    columnOrderWithParents: tableColumnOrder,
    uniqueParentIds: allLocalColumnsParentIds,
  } = generateColumnOrderWithParentIds(
    table.getColumn,
    table.getState().columnOrder,
  );

  const [activeFilterValue, setActiveFilterValue] = useState<string>('');

  // original column visibility state
  const initialColumnVisibility = useMemo(
    () => generateColumnVisibilityObject(leafColumnsWithoutBuiltin),
    [leafColumnsWithoutBuiltin],
  );

  // current column visibility state while modal is open
  const [localColumnVisibility, setLocalColumnVisibility] = useState<
    Record<string, boolean>
  >(initialColumnVisibility);

  // current column order state while modal is open
  const [localColumnOrder, setLocalColumnOrder] =
    useState<string[]>(tableColumnOrder);

  // current column pinning state while modal is open
  const [localColumnPinning, setLocalColumnPinning] =
    useState<ColumnPinningState>(
      // original column pinning state
      () => table.getState().columnPinning,
    );

  // flag whether the "Reset to default" status message for screen readers should be displayed
  const [showResetStatus, setShowResetStatus] = useState(false);

  // if controlled, call the callback to get resettable state. If that doesn't work or uncontrolled
  // use the evaluated original table default order/visibility state from options
  const columnOrderToReset =
    resetColumnOrder ?? table.options.originalColumnOrder;
  const columnVisibilityToReset =
    resetColumnVisibility ?? table.options.originalColumnVisibility;
  const columnPinningToReset =
    resetColumnPinning ?? table.options.originalColumnPinning;

  // Memoize filtered column order for comparison
  const filteredLocalColumnOrder = useMemo(
    () =>
      localColumnOrder.filter(
        (colId) => !allLocalColumnsParentIds.includes(colId),
      ),
    [localColumnOrder, allLocalColumnsParentIds],
  );

  // checks if local default column visibility has changed
  const hasVisibilityChanged = useMemo(
    () =>
      visibilitySettingsEnabled &&
      getHasVisibilityChanged(columnVisibilityToReset, localColumnVisibility),
    [visibilitySettingsEnabled, columnVisibilityToReset, localColumnVisibility],
  );

  // checks if local default column order has changed
  const hasColumnOrderChanged =
    columnOrderSettingsEnabled &&
    getHasColumnOrderChanged(columnOrderToReset, filteredLocalColumnOrder);

  // checks if local default column pinning state has changed
  const hasColumnPinningChanged =
    columnPinningSettingsEnabled &&
    getHasColumnPinningChanged(columnPinningToReset, localColumnPinning);

  // determines whether all columns are visible
  const showHideAllState = getCombinedVisibilityCheckboxState(
    localColumnVisibility,
    leafColumnsWithoutBuiltin,
  );

  /**
   * Updates the local column visibility state.
   * @param column - the column to update.
   * @param currentVisibilityState - the current visibility state of the column.
   */
  const updateLocalColumnVisibility = (
    column: Column<unknown, unknown>,
    currentVisibilityState: boolean | 'indeterminate',
  ): void => {
    const childColumns = column.columns;
    if (childColumns.length === 0) {
      setLocalColumnVisibility((prevState) => ({
        ...prevState,
        [column.id]: !currentVisibilityState,
      }));
    } else {
      setLocalColumnVisibility((prevState) =>
        getMultipleLocalColumnVisibility(
          childColumns,
          currentVisibilityState,
          prevState,
        ),
      );
    }
    showResetStatus && setShowResetStatus(false);
  };

  /**
   * Updates the local column order state.
   * @param newColumnOrder - the new column order.
   */
  const updateLocalColumnOrder = (newColumnOrder: string[]): void => {
    setLocalColumnOrder(newColumnOrder);
    showResetStatus && setShowResetStatus(false);
  };

  /**
   * Updates the local column pinning state.
   * @param newState - the new column pinning state.
   */
  const updateLocalColumnPinning = (newState: ColumnPinningState): void => {
    setLocalColumnPinning(newState);
    showResetStatus && setShowResetStatus(false);
  };

  const onShowHideAllChange = () => {
    setLocalColumnVisibility((prevState) =>
      getMultipleLocalColumnVisibility(
        leafColumnsWithoutBuiltin,
        showHideAllState,
        prevState,
      ),
    );
    showResetStatus && setShowResetStatus(false);
  };

  const showVisibilityControl =
    visibilitySettingsEnabled && activeFilterValue.length === 0;
  const showFilterControl =
    visibilitySettingsEnabled || columnPinningSettingsEnabled;

  const onReset = () => {
    let didReset = false;

    // reset visibility, order and/or pinning state if they are enabled
    if (visibilitySettingsEnabled && columnVisibilityToReset) {
      setLocalColumnVisibility(
        generateColumnVisibilityObject(
          leafColumnsWithoutBuiltin,
          columnVisibilityToReset,
        ),
      );
      didReset = true;
    }

    if (columnOrderSettingsEnabled && columnOrderToReset) {
      const { columnOrderWithParents } = generateColumnOrderWithParentIds(
        table.getColumn,
        columnOrderToReset,
      );

      setLocalColumnOrder(columnOrderWithParents);
      didReset = true;
    }

    if (columnPinningSettingsEnabled && columnPinningToReset) {
      setLocalColumnPinning(columnPinningToReset);
      didReset = true;
    }

    // if settings were successfully reset,
    // show a status message for the screen reader
    didReset && setShowResetStatus(true);
  };

  const onApply = () => {
    table.setColumnVisibility(localColumnVisibility);
    table.setColumnOrder(filteredLocalColumnOrder);
    table.setColumnPinning(localColumnPinning);
    onModalClose();
  };

  return (
    <DataTableSettingsModal
      title={intl.formatMessage(dataTableMessages.columnSettingsTitle)}
      show={true}
      showResetStatus={showResetStatus}
      onDismiss={onModalClose}
      onReset={onReset}
      onApply={onApply}
      disableButtons={{
        reset:
          !hasColumnOrderChanged &&
          !hasVisibilityChanged &&
          !hasColumnPinningChanged,
      }}
      controlLabels={{
        reset: intl.formatMessage(
          dataTableMessages.resetColumnSettingsButtonText,
        ),
        cancel: intl.formatMessage(dataTableMessages.cancelButtonText),
        apply: intl.formatMessage(dataTableMessages.applyButtonText),
      }}
    >
      <DataTableVisibilityFilterControls
        showVisibilityControl={showVisibilityControl}
        showFilterControl={showFilterControl}
        setActiveFilterValue={setActiveFilterValue}
        showHideAllState={showHideAllState}
        onShowHideAllChange={onShowHideAllChange}
      />
      <DataTableColumnsList
        visibilitySettings={visibilitySettingsEnabled}
        columnOrderSettings={columnOrderSettingsEnabled}
        columnPinningSettings={columnPinningSettingsEnabled}
        activeFilterValue={activeFilterValue}
        localColumnVisibility={localColumnVisibility}
        updateLocalColumnVisibility={updateLocalColumnVisibility}
        localColumnOrder={localColumnOrder}
        updateLocalColumnOrder={updateLocalColumnOrder}
        localColumnPinning={localColumnPinning}
        updateLocalColumnPinning={updateLocalColumnPinning}
        hasFilterBar={showFilterControl || showVisibilityControl}
      />
    </DataTableSettingsModal>
  );
};

function DataTableColumnSettingsModalFwd(
  props: DataTableColumnSettingsModalProps,
  forwardedRef: Ref<DataTableColumnSettingsModalRef>,
): JSX.Element | null {
  const [modalOpen, setModalOpen] = useState(false);
  const [columnSettingsOverride, setColumnSettingsOverride] =
    useState<DataTableColumnSettingsConfig>();

  // open the modal from outside
  useImperativeHandle(forwardedRef, () => ({
    openColumnSettings: (columnSettings?: DataTableColumnSettingsConfig) => {
      setColumnSettingsOverride(columnSettings);
      setModalOpen(true);
    },
  }));

  if (modalOpen) {
    return (
      <ColumnSettingsModal
        {...props}
        columnSettingsOverride={columnSettingsOverride}
        onModalClose={() => setModalOpen(false)}
      />
    );
  }
  return null;
}

/**
 * @internal
 */
export const DataTableColumnSettingsModal = forwardRef(
  DataTableColumnSettingsModalFwd,
);
